class TesterModal extends HTMLElement {
  constructor() {
    super();
    this.handleKeyUp = this.handleKeyUp.bind(this);
  }

  connectedCallback() {
    this.overlay = this.querySelector('.tester-modal-overlay');
    this.trigger = this.querySelector('[data-tester-trigger]');
    
    if (this.overlay && !this.overlay.isMoved) {
      this.overlay.isMoved = true;
      document.body.appendChild(this.overlay);
    }

    this.trigger?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.open();
    });
    
    this.overlay?.addEventListener('click', this.handleOverlayClick.bind(this));

    document.addEventListener('keyup', this.handleKeyUp);
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.handleKeyUp);
    
    // Clean up the overlay from document.body to prevent duplication
    if (this.overlay && this.overlay.isMoved) {
      this.overlay.classList.remove('is-open');
      if (this.overlay.parentNode === document.body) {
        document.body.removeChild(this.overlay);
      }
      this.overlay.isMoved = false;
    }
    
    document.body.classList.remove('overflow-hidden');
  }

  handleKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.close();
    }
  }

  handleOverlayClick(event) {
    if (
      event.target.closest('[data-tester-close]') ||
      event.target.classList.contains('tester-modal-overlay')
    ) {
      this.close();
    }
  }

 open() {
  if (!this.overlay) return;
  this.overlay.classList.add('is-open');
  document.body.classList.add('overflow-hidden');

  // Hide only top-level siblings of the modal's root, not everything
  document.querySelectorAll('body > *').forEach(el => {
    if (!el.contains(this.overlay)) {
      el._hadAriaHidden = el.hasAttribute('aria-hidden'); // track prior state
      el.setAttribute('aria-hidden', 'true');
    }
  });

  const container = this.overlay.querySelector('.tester-modal-container');
  if (container) {
    const focusable = Array.from(
      container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-"])')
    ).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0);
    if (focusable.length > 0) {
      setTimeout(() => focusable[0].focus(), 50);
      container.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          const active = document.activeElement;
          if (e.shiftKey && active === focusable[0]) {
            e.preventDefault();
            focusable[focusable.length - 1].focus();
          } else if (!e.shiftKey && active === focusable[focusable.length - 1]) {
            e.preventDefault();
            focusable[0].focus();
          }
        }
      });
    }
  }

  const video = this.overlay.querySelector('video');
  if (video) {
    video.load();
    video.muted = true;

    const customVideo = this.overlay.querySelector('custom-video');
    if (customVideo) {
      customVideo.classList.remove('is-paused');
      
      // Handle programmatic autoplay delay if configured
      const shouldAutoplay = customVideo.getAttribute('data-autoplay') === 'true';
      const delayAttr = customVideo.getAttribute('data-autoplay-delay');
      
      if (shouldAutoplay) {
        const delayTime = delayAttr ? parseInt(delayAttr, 10) : 0;
        setTimeout(() => {
          video.play?.().catch(() => {});
        }, delayTime);
      } else {
        customVideo.classList.add('is-paused');
        const playBtn = customVideo.querySelector('.play-pause');
        if (playBtn) {
          playBtn.setAttribute('data-video-play', 'true');
          playBtn.setAttribute('aria-label', 'Play video');
        }
      }
    }
  }
}

  close() {
    if (!this.overlay || !this.overlay.classList.contains('is-open')) return;
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.trigger?.focus();

    // Pause and mute video if exists inside modal
    const video = this.overlay.querySelector('video');
    if (video) {
      video.pause();
      video.muted = true;
      const customVideo = this.overlay.querySelector('custom-video');
      if (customVideo) {
        customVideo.classList.add('is-paused');
        const playBtn = customVideo.querySelector('.play-pause');
        if (playBtn) {
          playBtn.setAttribute('data-video-play', 'true');
          playBtn.setAttribute('aria-label', 'Play video');
        }
        const muteBtn = customVideo.querySelector('.mute');
        if (muteBtn) {
          muteBtn.setAttribute('data-mute', 'true');
          muteBtn.setAttribute('aria-label', 'Unmute video');
        }
      }
    }

    // Restore only what we hid — don't blindly removeAttr from everything
    document.querySelectorAll('body > *').forEach(el => {
      if (!el.contains(this.overlay)) {
        if (!el._hadAriaHidden) {
          el.removeAttribute('aria-hidden');
        }
        delete el._hadAriaHidden;
      }
    });
  }
}

if (!customElements.get('tester-modal')) {
  customElements.define('tester-modal', TesterModal);
}
